import React, { useEffect, useMemo, useRef, useState } from "react";
import { Download, FileText, FileSpreadsheet, ChevronDown, Columns3, Save, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const LAYOUTS_KEY = "flow_export_layouts_v1";
const LAST_LAYOUT_KEY = "flow_export_last_layout_v1";
const ORDER_KEY = "flow_export_column_order_v1";  // per-scope drag order

function readAllLayouts() {
  try { return JSON.parse(localStorage.getItem(LAYOUTS_KEY) || "{}") || {}; }
  catch { return {}; }
}
function writeAllLayouts(v) {
  try { localStorage.setItem(LAYOUTS_KEY, JSON.stringify(v)); } catch { /* ignore */ }
}
function readLastLayout(scope) {
  try {
    const all = JSON.parse(localStorage.getItem(LAST_LAYOUT_KEY) || "{}") || {};
    const v = all[scope];
    return Array.isArray(v) ? v : null;
  } catch { return null; }
}
function writeLastLayout(scope, cols) {
  try {
    const all = JSON.parse(localStorage.getItem(LAST_LAYOUT_KEY) || "{}") || {};
    all[scope] = cols;
    localStorage.setItem(LAST_LAYOUT_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
}
function readColumnOrder(scope) {
  try {
    const all = JSON.parse(localStorage.getItem(ORDER_KEY) || "{}") || {};
    const v = all[scope];
    return Array.isArray(v) ? v : null;
  } catch { return null; }
}
function writeColumnOrder(scope, keys) {
  try {
    const all = JSON.parse(localStorage.getItem(ORDER_KEY) || "{}") || {};
    all[scope] = keys;
    localStorage.setItem(ORDER_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
}

function byHasKey(cols, key) {
  return Array.isArray(cols) && cols.some((c) => c.key === key);
}

/**
 * FilteredExportButton — dropdown that POSTs a list of filtered
 * project IDs to /pm/projects-export/filtered and triggers a browser
 * download for CSV / XLSX / PDF. Column Chooser lets users tick which
 * columns are included, DRAG rows to change output order, and save the
 * layout as a named preset per user (scope).
 */
export default function FilteredExportButton({
  ids, title = "Filtered projects", disabled = false, testIdPrefix = "export",
  layoutScope,    // stable key for per-scope saved layouts (defaults to testIdPrefix)
}) {
  const scope = layoutScope || testIdPrefix;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [showColumns, setShowColumns] = useState(false);
  // `catalog` is the ORDERED list of {key,label} shown in the chooser.
  // Drag reordering rewrites this array; the export request iterates
  // `catalog` and filters by `selected`, so drag order = output order.
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [layouts, setLayouts] = useState(() => readAllLayouts()[scope] || {});
  const [dragKey, setDragKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const rootRef = useRef(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) { setOpen(false); setShowColumns(false); }
    };
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); setShowColumns(false); } };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Load the column catalog on first open so we don't hit the endpoint
  // for users who never click Export. Applies the saved per-scope drag
  // order on top of the server catalog, then honours the last-used
  // ticked subset if there is one.
  useEffect(() => {
    if (!open || catalog.length > 0) return;
    api.get("/pm/projects-export/columns")
      .then((r) => {
        const cols = r.data?.columns || [];
        const order = readColumnOrder(scope);
        let ordered = cols;
        if (order && order.length) {
          const byKey = new Map(cols.map((c) => [c.key, c]));
          const seen = new Set();
          const head = [];
          for (const k of order) {
            const c = byKey.get(k);
            if (c && !seen.has(k)) { head.push(c); seen.add(k); }
          }
          // Append any brand-new columns that weren't in the saved order
          // (keeps existing users forward-compatible when we add columns).
          const tail = cols.filter((c) => !seen.has(c.key));
          ordered = [...head, ...tail];
        }
        setCatalog(ordered);
        const last = readLastLayout(scope);
        if (last && last.length > 0) {
          const valid = new Set(last.filter((k) => byHasKey(ordered, k)));
          setSelected(valid.size ? valid : new Set(ordered.map((c) => c.key)));
        } else {
          setSelected(new Set(ordered.map((c) => c.key)));
        }
      })
      .catch(() => { /* fall back to server default (all cols) */ });
  }, [open, catalog.length, scope]);

  const download = async (format) => {
    if (!ids || ids.length === 0) {
      toast.error("Nothing to export — filters have no matching projects.");
      return;
    }
    setBusy(format);
    try {
      // Iterate catalog (drag order) and keep only ticked keys — this
      // becomes the exact column order in the exported file.
      const orderedKeys = catalog.filter((c) => selected.has(c.key)).map((c) => c.key);
      const cols = orderedKeys.length > 0 ? orderedKeys : undefined;
      const res = await api.post(
        "/pm/projects-export/filtered",
        { ids, format, title, columns: cols },
        { responseType: "blob" },
      );
      if (cols) {
        writeLastLayout(scope, cols);
        writeColumnOrder(scope, catalog.map((c) => c.key));
      }
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const fallback = `${title}.${format === "xlsx" ? "xlsx" : format}`;
      const filename = match ? match[1] : fallback;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${filename} · ${ids.length} row${ids.length === 1 ? "" : "s"}`);
      setOpen(false); setShowColumns(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || `Could not build ${format.toUpperCase()}`);
    } finally { setBusy(null); }
  };

  const toggleCol = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(catalog.map((c) => c.key)));
  const selectNone = () => setSelected(new Set());

  const saveLayout = () => {
    const name = window.prompt("Save this column layout as… (name)");
    if (!name) return;
    const clean = name.trim().slice(0, 60);
    // Save the ORDERED subset of ticked keys as they appear in the
    // catalog — applying the layout later restores both selection and
    // order.
    const orderedKeys = catalog.filter((c) => selected.has(c.key)).map((c) => c.key);
    if (!clean || !orderedKeys.length) {
      toast.error("Pick at least one column and give the layout a name.");
      return;
    }
    const all = readAllLayouts();
    all[scope] = { ...(all[scope] || {}), [clean]: orderedKeys };
    writeAllLayouts(all);
    setLayouts(all[scope]);
    toast.success(`Layout "${clean}" saved`);
  };
  const applyLayout = (name) => {
    const cols = layouts[name];
    if (!cols) return;
    // Reorder the catalog so ticked columns come first in the layout's
    // saved order; append the rest at the end untouched. Then set the
    // ticked set to exactly the layout's keys.
    const byKey = new Map(catalog.map((c) => [c.key, c]));
    const seen = new Set();
    const head = [];
    for (const k of cols) {
      const c = byKey.get(k);
      if (c && !seen.has(k)) { head.push(c); seen.add(k); }
    }
    const tail = catalog.filter((c) => !seen.has(c.key));
    setCatalog([...head, ...tail]);
    setSelected(new Set(cols.filter((k) => byKey.has(k))));
    toast.success(`Applied layout "${name}"`);
  };
  const deleteLayout = (name) => {
    if (!window.confirm(`Delete layout "${name}"?`)) return;
    const all = readAllLayouts();
    if (all[scope]) delete all[scope][name];
    writeAllLayouts(all);
    setLayouts({ ...(all[scope] || {}) });
  };

  // ---- Drag-and-drop reorder inside the chooser (native HTML5) ----
  const onDragStart = (e, key) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", key); } catch { /* Safari */ }
  };
  const onDragOver = (e, key) => {
    if (!dragKey || dragKey === key) return;
    e.preventDefault();
    setDragOverKey(key);
  };
  const onDrop = (e, targetKey) => {
    e.preventDefault();
    if (!dragKey || dragKey === targetKey) { setDragKey(null); setDragOverKey(null); return; }
    const fromIdx = catalog.findIndex((c) => c.key === dragKey);
    const toIdx = catalog.findIndex((c) => c.key === targetKey);
    if (fromIdx < 0 || toIdx < 0) { setDragKey(null); setDragOverKey(null); return; }
    const next = [...catalog];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setCatalog(next);
    writeColumnOrder(scope, next.map((c) => c.key));
    setDragKey(null);
    setDragOverKey(null);
  };
  const onDragEnd = () => { setDragKey(null); setDragOverKey(null); };

  const count = ids?.length || 0;
  const selCount = selected.size;
  const layoutNames = useMemo(() => Object.keys(layouts).sort(), [layouts]);

  return (
    <div className="relative inline-block" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled || count === 0}
        className="btn-ghost text-xs"
        title={count === 0
          ? "No projects match the current filters"
          : `Export ${count} visible project${count === 1 ? "" : "s"}`}
        data-testid={`${testIdPrefix}-btn`}
      >
        <Download size={13} /> Export {count > 0 ? `(${count})` : ""}
        <ChevronDown size={11} className={`ml-0.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 z-40 bg-white border border-[var(--line)] rounded shadow-lg min-w-[220px]"
          data-testid={`${testIdPrefix}-menu`}
        >
          <button type="button" onClick={() => download("csv")} disabled={busy !== null}
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs hover:bg-[var(--surface-2,#F6F7F9)]"
                  data-testid={`${testIdPrefix}-csv`}>
            <FileText size={13} /> CSV {busy === "csv" && "…"}
          </button>
          <button type="button" onClick={() => download("xlsx")} disabled={busy !== null}
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs hover:bg-[var(--surface-2,#F6F7F9)]"
                  data-testid={`${testIdPrefix}-xlsx`}>
            <FileSpreadsheet size={13} /> Excel (.xlsx) {busy === "xlsx" && "…"}
          </button>
          <button type="button" onClick={() => download("pdf")} disabled={busy !== null}
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs hover:bg-[var(--surface-2,#F6F7F9)]"
                  data-testid={`${testIdPrefix}-pdf`}>
            <FileText size={13} /> PDF {busy === "pdf" && "…"}
          </button>
          <div className="border-t border-[var(--line-soft,#EEE)]" />
          <button type="button" onClick={() => setShowColumns((v) => !v)}
                  className="flex items-center justify-between w-full px-3 py-2 text-xs hover:bg-[var(--surface-2,#F6F7F9)]"
                  data-testid={`${testIdPrefix}-columns-toggle`}>
            <span className="flex items-center gap-2"><Columns3 size={13} /> Columns</span>
            <span className="muted font-mono">{selCount}/{catalog.length || "…"}</span>
          </button>
          {showColumns && (
            <div className="border-t border-[var(--line-soft,#EEE)] p-3 max-w-[280px]"
                 data-testid={`${testIdPrefix}-columns-panel`}>
              {catalog.length === 0 ? (
                <div className="text-xs muted italic">Loading columns…</div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-widest muted">
                    <span>Include columns</span>
                    <div className="flex items-center gap-1.5">
                      <button type="button" onClick={selectAll} className="hover:underline"
                              data-testid={`${testIdPrefix}-columns-all`}>All</button>
                      <span>·</span>
                      <button type="button" onClick={selectNone} className="hover:underline"
                              data-testid={`${testIdPrefix}-columns-none`}>None</button>
                    </div>
                  </div>
                  <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
                    {catalog.map((c) => {
                      const isDragging = dragKey === c.key;
                      const isTarget = dragOverKey === c.key;
                      return (
                        <div
                          key={c.key}
                          draggable
                          onDragStart={(e) => onDragStart(e, c.key)}
                          onDragOver={(e) => onDragOver(e, c.key)}
                          onDrop={(e) => onDrop(e, c.key)}
                          onDragEnd={onDragEnd}
                          className={`flex items-center gap-2 text-xs px-1 py-0.5 rounded border ${
                            isTarget
                              ? "border-[var(--accent-hover)] bg-[var(--surface-2,#F6F7F9)]"
                              : "border-transparent hover:bg-[var(--surface-2,#F6F7F9)]"
                          } ${isDragging ? "opacity-40" : ""}`}
                          data-testid={`${testIdPrefix}-columns-row-${c.key}`}
                        >
                          <span
                            className="cursor-grab active:cursor-grabbing muted hover:text-[var(--ink)]"
                            title="Drag to reorder"
                            aria-label={`Drag ${c.label} to reorder`}
                            data-testid={`${testIdPrefix}-columns-drag-${c.key}`}
                          >
                            <GripVertical size={12} />
                          </span>
                          <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                            <input type="checkbox"
                                   checked={selected.has(c.key)}
                                   onChange={() => toggleCol(c.key)}
                                   data-testid={`${testIdPrefix}-columns-check-${c.key}`} />
                            <span className="truncate">{c.label}</span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t border-[var(--line-soft,#EEE)] mt-3 pt-2">
                    <div className="text-[10px] uppercase tracking-widest muted mb-1">Saved layouts</div>
                    {layoutNames.length === 0 ? (
                      <div className="text-[11px] muted italic">Save the current selection to reuse it later.</div>
                    ) : (
                      <ul className="mb-2 space-y-0.5">
                        {layoutNames.map((n) => (
                          <li key={n} className="flex items-center justify-between gap-2 text-xs"
                              data-testid={`${testIdPrefix}-layout-${n}`}>
                            <button type="button" onClick={() => applyLayout(n)}
                                    className="flex-1 text-left hover:underline truncate"
                                    data-testid={`${testIdPrefix}-layout-apply-${n}`}>
                              {n}
                            </button>
                            <button type="button" onClick={() => deleteLayout(n)}
                                    className="muted hover:text-red-700"
                                    title={`Delete layout "${n}"`}
                                    data-testid={`${testIdPrefix}-layout-delete-${n}`}>
                              <Trash2 size={11} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <button type="button" onClick={saveLayout}
                            className="btn-ghost !py-1 !text-[11px] w-full justify-center"
                            data-testid={`${testIdPrefix}-columns-save`}>
                      <Save size={11} /> Save current as layout…
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
